import { isNullOrUndefined as isNOU } from '@syncfusion/ej2-base';
import { getBlockContentElement, getBlockIndexById, getBlockModelById } from '../../common/utils/block';
import { setCursorPosition, getNodeFromPath, captureSelectionState, getTextOffset } from '../../common/utils/selection';
import { findClosestParent } from '../../common/utils/dom';
import { actionType, BLOCK_CLS, events } from '../../common/constant';
import { decoupleReference } from '../../common/utils/common';
import { BlockType } from '../../models/enums';
import { UndoRedoManager } from '../plugins/common/undo-manager';
/**
 * `UndoRedoManager` module is used to handle undo and redo actions.
 */
export class UndoRedoAction {
    constructor(manager) {
        /** @hidden */
        this.undoRedoStack = [];
        /** @hidden */
        this.index = -1; // points to the last applied action; -1 means no actions applied
        /** @hidden */
        this.isUndoing = false;
        /** @hidden */
        this.isRedoing = false;
        this.preventRestores = new Set([
            actionType.tableRowInserted, actionType.tableRowDeleted, actionType.tableColumnDeleted, actionType.tableColumnInserted,
            actionType.tableCellsCleared,
            actionType.blockAdded, actionType.blockRemoved, actionType.blockMoved, actionType.formattingAction,
            actionType.blockTransformed, actionType.multipleBlocksTransformed
        ]);
        /** @hidden */
        this.isBatchMode = false;
        /** @hidden */
        this.batchedTransforms = [];
        this.parent = manager;
        this.undoRedoManager = new UndoRedoManager(this.parent, this);
        this.addEventListener();
    }
    addEventListener() {
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    removeEventListener() {
        this.parent.observer.off(events.destroy, this.destroy);
    }
    /**
     * Begins a batch mode for tracking multiple block transformations as a single undo action
     *
     * @returns {void}
     * @hidden
     */
    beginBatchTransform() {
        this.isBatchMode = true;
        this.batchedTransforms = [];
    }
    /**
     * Resets the batch mode state and clears batched transformations
     *
     * @returns {void}
     * @private
     */
    resetBatchState() {
        this.isBatchMode = false;
        this.batchedTransforms = [];
    }
    /**
     * Ends batch mode and pushes all accumulated block transformations as a single undo entry
     *
     * @returns {void}
     * @hidden
     */
    endBatchTransform() {
        if (!this.isBatchMode || this.batchedTransforms.length === 0) {
            this.resetBatchState();
            return;
        }
        this.pushActionIntoUndoStack({
            action: actionType.multipleBlocksTransformed,
            data: {
                transformedBlocks: decoupleReference(this.batchedTransforms)
            }
        });
        this.resetBatchState();
    }
    /**
     * Checks if batch mode is currently active
     *
     * @returns {boolean} True if in batch mode
     * @hidden
     */
    getIsBatchMode() {
        return this.isBatchMode;
    }
    /**
     * Handles the undo operation.
     *
     * @returns {void}
     * @hidden
     */
    undo() {
        this.performUndoRedo(true, 'isUndoing');
    }
    /**
     * Handles the redo operation.
     *
     * @returns {void}
     * @hidden
     */
    redo() {
        this.performUndoRedo(false, 'isRedoing');
    }
    /**
     * Executes undo or redo operation, updating editor state and notifying observers.
     * Processes sibling actions for formatting or paste, then applies the target state.
     *
     * @param {boolean} isUndo - True for undo, false for redo.
     * @param {string} flagName - State flag ('isUndoing' or 'isRedoing') to prevent recursion.
     * @returns {void}
     */
    performUndoRedo(isUndo, flagName) {
        this.parent.selectionOverlay.clearSelectionOverlay();
        this.parent.inlineToolbarModule.hideInlineToolbar();
        const canPerform = isUndo ? this.canUndo() : this.canRedo();
        if (!canPerform) {
            return;
        }
        this[`${flagName}`] = true;
        const siblingAction = this.undoRedoStack[this.index + 2];
        const shouldProcessSiblingAction = !isUndo && siblingAction !== undefined && (
        // Case 1: Formatting while typing (Press ctrlB and type)
        siblingAction.data.isTypingWithFormat ||
            // Case 2: Clipboard paste - (with blocks) or (selective paste)
            (siblingAction.action === 'clipboardPaste' &&
                siblingAction.data.type === 'blocks'
                ||
                    siblingAction.data.isSelectivePaste));
        this.index = shouldProcessSiblingAction ? this.index + 1 : this.index;
        const state = isUndo ? this.undoRedoStack[this.index] : this.undoRedoStack[this.index + 1];
        if (isUndo) {
            this.updateFileUploadPropsOnStack(state);
        }
        if (state) {
            // Process the action first
            this.processUndoRedoAction(state);
            // Then move the pointer
            this.index = isUndo ? this.index - 1 : this.index + 1;
            const eventArgs = {
                isUndo,
                content: isUndo ? state.oldBlockModel : state.updatedBlockModel,
                previousContent: isUndo ? state.updatedBlockModel : state.oldBlockModel
            };
            this.parent.observer.notify('undoRedoPerformed', eventArgs);
            this.parent.listPlugin.recalculateMarkersForListItems();
            this.parent.stateManager.updateManagerBlocks();
        }
        this[`${flagName}`] = false;
    }
    /**
     * Pushes the given state into the undo stack
     *
     * @param {IUndoRedoState} state - The current state.
     * @returns {void}
     * @hidden
     */
    pushActionIntoUndoStack(state) {
        const isSelectionPresent = !isNOU(this.parent.previousSelection);
        const isTextSelected = isSelectionPresent && !this.parent.previousSelection.isCollapsed;
        const currentState = Object.assign({}, state, { undoSelection: isSelectionPresent ? this.parent.previousSelection : captureSelectionState(), redoSelection: ((isTextSelected && !(state.action === actionType.formattingAction))
                || (isSelectionPresent && state.action === actionType.blockTransformed))
                ? this.parent.previousSelection
                : captureSelectionState() });
        // Truncate any future states if we are not at the end
        if (this.index < this.undoRedoStack.length - 1) {
            this.undoRedoStack = this.undoRedoStack.slice(0, this.index + 1);
        }
        // Push new state
        this.undoRedoStack.push(currentState);
        // Enforce max size
        if (this.undoRedoStack.length > this.parent.undoRedoStack) {
            const excess = this.undoRedoStack.length - this.parent.undoRedoStack;
            this.undoRedoStack = this.undoRedoStack.slice(excess);
            this.index = Math.max(this.index - excess, -1);
        }
        // Point to the last item
        this.index = this.undoRedoStack.length - 1;
        this.parent.previousSelection = undefined;
    }
    /**
     * Processes the undo/redo action.
     *
     * @param {IUndoRedoState} currentState - The current undo/redo state.
     * @returns {void}
     * @hidden
     */
    processUndoRedoAction(currentState) {
        this.parent.floatingIconAction.hideFloatingIcons();
        /* Perform core actions */
        this.applyUndoRedoChange(currentState);
        /* UI and selection refresh */
        const action = currentState.action;
        const shouldPreventRestore = this.preventRestores.has(action);
        if (!shouldPreventRestore) {
            this.restoreSelectionState(currentState);
        }
        if ((action === actionType.contentChanged || action === actionType.lineBreakAdded) && this.parent.currentFocusedBlock) {
            this.parent.floatingIconAction.showFloatingIcons(this.parent.currentFocusedBlock);
        }
        this.parent.refreshPlaceholder();
    }
    applyUndoRedoChange(currentState) {
        switch (currentState.action) {
            case actionType.contentChanged: {
                const blockId = currentState.data ? currentState.data.blockId : '';
                this.undoRedoManager.reRenderWithPreviousState(blockId, currentState.oldBlockModel, currentState.updatedBlockModel);
                break;
            }
            case actionType.formattingAction:
                this.undoRedoManager.handleFormattingUndoRedo(currentState);
                break;
            case actionType.isExpanded: {
                const blockElement = this.parent.getBlockElementById(currentState.data.blockId);
                const updatedState = this.isUndoing ? !currentState.data.isExpanded
                    : currentState.data.isExpanded;
                this.parent.blockRenderer.collapsibleRenderer.updateCollapsibleBlockExpansion(blockElement, updatedState, true);
                break;
            }
            case actionType.indent: {
                if (this.isUndoing) {
                    this.parent.execCommand({ command: 'IndentBlock', state: {
                            blockIDs: currentState.data.blockIDs,
                            shouldDecrease: !currentState.data.shouldDecrease,
                            isUndoRedoAction: true
                        } });
                }
                else {
                    this.parent.execCommand({ command: 'IndentBlock', state: {
                            blockIDs: currentState.data.blockIDs,
                            shouldDecrease: currentState.data.shouldDecrease,
                            isUndoRedoAction: true
                        } });
                }
                break;
            }
            case actionType.checked: {
                const block = getBlockModelById(currentState.data.blockId, this.parent.getEditorBlocks());
                const updatedState = this.isUndoing ? !currentState.data.isChecked
                    : currentState.data.isChecked;
                this.parent.blockRenderer.listRenderer.toggleCheckedState(block, updatedState, true);
                break;
            }
            case actionType.lineBreakAdded: {
                const state = currentState.data;
                const blockModel = getBlockModelById(state.blockId, this.parent.getEditorBlocks());
                const oldBlock = decoupleReference(blockModel);
                this.parent.blockService.updateContent(blockModel.id, this.isUndoing ? currentState.oldContents : currentState.newContents);
                this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                        data: [{ block: blockModel, oldBlock: oldBlock }]
                    } });
                break;
            }
            case actionType.blockAdded:
            case actionType.blockRemoved:
                this.undoRedoManager.blockAdditionDeletionUndoRedo(currentState);
                this.parent.floatingIconAction.showFloatingIcons(this.parent.currentFocusedBlock);
                break;
            case actionType.blockMoved:
                this.undoRedoManager.handleBlockMovement(currentState);
                break;
            case actionType.multipleBlocksDeleted:
                this.undoRedoManager.handleMultipleBlocksUndoRedo(currentState);
                break;
            case actionType.blockTransformed:
                this.undoRedoManager.reTransformBlocks(currentState);
                break;
            case actionType.multipleBlocksTransformed:
                this.undoRedoManager.reTransformMultipleBlocks(currentState);
                break;
            case actionType.imageInsertion:
                this.undoRedoManager.handleImageInsertion(currentState);
                break;
            case actionType.clipboardPaste:
                this.undoRedoManager.handleClipboardActions(currentState);
                break;
            case actionType.tableRowInserted:
                this.undoRedoManager.processRowAction(currentState.data, 'insert');
                break;
            case actionType.tableRowDeleted:
                this.undoRedoManager.processRowAction(currentState.data, 'delete');
                break;
            case actionType.tableColumnInserted:
                this.undoRedoManager.processColumnAction(currentState.data, 'insert');
                break;
            case actionType.tableColumnDeleted:
                this.undoRedoManager.processColumnAction(currentState.data, 'delete');
                break;
            case actionType.tableRowsDeleted:
                this.undoRedoManager.handleBulkRowsDeleted(currentState);
                break;
            case actionType.tableColumnsDeleted:
                this.undoRedoManager.handleBulkColumnsDeleted(currentState);
                break;
            case actionType.tableCellsCleared:
                this.undoRedoManager.handleTableCellsCleared(currentState);
                break;
            case actionType.tableCellsPasted:
                this.undoRedoManager.handleTableCellsPasted(currentState);
                break;
            case actionType.tableHeaderInput:
                this.undoRedoManager.handleTableHeaderUndoRedo(currentState);
                break;
            case actionType.tableColumnResized:
                this.undoRedoManager.handleTableColumnResized(currentState);
                break;
        }
    }
    /**
     * Restores the selection after undo/redo action.
     *
     * @param {IUndoRedoState} currentState - The current undo/redo state.
     * @returns {void}
     * @hidden
     */
    restoreSelectionState(currentState) {
        const selection = this.isUndoing ? currentState.undoSelection : currentState.redoSelection;
        if (selection) {
            const startBlock = this.parent.rootEditorElement.querySelector('#' + selection.startBlockId);
            const endBlock = this.parent.rootEditorElement.querySelector('#' + selection.endBlockId);
            if (startBlock && endBlock) {
                const startNode = getNodeFromPath(startBlock, selection.startContainerPath);
                const endNode = getNodeFromPath(endBlock, selection.endContainerPath);
                const isSelectivePaste = currentState.action === actionType.clipboardPaste
                    && currentState.data.isSelectivePaste;
                const canRestoreForActions = currentState.action === actionType.indent
                    || currentState.action === actionType.formattingAction
                    || currentState.action === actionType.multipleBlocksDeleted;
                if ((this.isUndoing || (canRestoreForActions && this.isRedoing))
                    && !selection.isCollapsed && startNode && endNode) {
                    this.parent.nodeSelection.createRangeWithOffsets(startNode, endNode, selection.startOffset, selection.endOffset);
                }
                else if (!isSelectivePaste && ((this.isRedoing && startNode) || selection.isCollapsed && startNode)) {
                    const blockElement = findClosestParent(startNode, '.' + BLOCK_CLS);
                    this.parent.setFocusToBlock(blockElement);
                    const contentElement = getBlockContentElement(blockElement);
                    const nodeBaseOffset = getTextOffset(startNode, contentElement);
                    const absoluteOffset = nodeBaseOffset + selection.startOffset;
                    setCursorPosition(contentElement, absoluteOffset);
                }
            }
        }
        else {
            // Fallback
            if (this.parent.currentFocusedBlock && document.contains(this.parent.currentFocusedBlock)) {
                const contentElement = getBlockContentElement(this.parent.currentFocusedBlock);
                if (contentElement) {
                    this.parent.setFocusToBlock(this.parent.currentFocusedBlock);
                    const position = contentElement.textContent.length;
                    setCursorPosition(contentElement, position);
                }
            }
        }
    }
    updateFileUploadPropsOnStack(state) {
        /* The block model stored on stack might not have proper src since it is pushed before user uploads
            a file. Hence we are updating here as it surely has updated src in current block model in the editor */
        const validTypes = [BlockType.Image];
        const blockModel = state.action === actionType.blockAdded ?
            state.oldBlockModel : state.data.newBlockModel;
        const isValid = ((state.action === actionType.blockTransformed
            || state.action === actionType.blockAdded || state.action === actionType.imageInsertion)
            && validTypes.indexOf(blockModel.blockType) !== -1);
        if (isValid) {
            // Update the props such as src into the stack
            const currentUpdatedBlock = getBlockModelById(blockModel.id, this.parent.getEditorBlocks());
            blockModel.properties = Object.assign({}, currentUpdatedBlock.properties);
        }
    }
    trackCheckedStateForUndoRedo(blockId, state) {
        this.pushActionIntoUndoStack({
            action: actionType.checked,
            data: { blockId: blockId, isChecked: state }
        });
    }
    trackContentChangedForUndoRedo(oldBlock, updatedBlock) {
        this.pushActionIntoUndoStack({
            oldBlockModel: oldBlock,
            updatedBlockModel: updatedBlock,
            action: actionType.contentChanged,
            data: { blockId: updatedBlock.id }
        });
    }
    trackFormattingForUndoRedo(blockIDs, oldBlockModels, updatedBlockModels, isTypingWithFormat, selection) {
        this.pushActionIntoUndoStack({
            action: actionType.formattingAction,
            data: {
                blockIDs,
                oldBlockModels,
                updatedBlockModels,
                isTypingWithFormat,
                selectionState: selection
            }
        });
    }
    /**
     * Handles undo/redo recording for block addition
     *
     * @param {IAddBlockInteraction} args - The arguments for adding a block.
     * @param {BlockModel} blockModel - The block model.
     * @returns {void}
     * @hidden
     */
    trackBlockAdditionForUndoRedo(args, blockModel) {
        if (args.isUndoRedoAction) {
            return;
        }
        const decoupledBlock = decoupleReference(blockModel);
        const targetBlockModel = args.targetBlockModel ? decoupleReference(args.targetBlockModel) : null;
        const blockBeforeSplit = args.blockBeforeSplit ? decoupleReference(args.blockBeforeSplit) : null;
        this.pushActionIntoUndoStack({
            action: actionType.blockAdded,
            data: {
                blockId: blockModel.id,
                currentIndex: getBlockIndexById(blockModel.id, this.parent.getEditorBlocks()),
                isSplitting: args.isSplitting,
                isAfter: args.isAfter,
                blockBeforeSplit: blockBeforeSplit,
                blocksAfterSplit: [targetBlockModel, decoupledBlock]
            },
            oldBlockModel: decoupledBlock
        });
    }
    /**
     * Records block removal for undo/redo
     *
     * @param {IDeleteBlockInteraction} args - The arguments for deleting a block.
     * @param {string} blockId - The ID of the block.
     * @param {BlockModel} blockModel - The block model.
     * @param {number} blockIndex - The index of the block.
     * @returns {void}
     * @hidden
     */
    trackBlockRemovalForUndoRedo(args, blockId, blockModel, blockIndex) {
        if (args.isUndoRedoAction) {
            return;
        }
        this.pushActionIntoUndoStack({
            action: actionType.blockRemoved,
            data: {
                blockId: blockId,
                currentIndex: blockIndex,
                isSplitting: args.isSplitting,
                isTargetDeletion: args.isTargetDeletion,
                blocksAfterSplit: args.blocksAfterSplit,
                blockBeforeSplit: args.blockBeforeSplit
            },
            oldBlockModel: decoupleReference(blockModel)
        });
    }
    /**
     * Records a block move operation for undo/redo
     *
     * @param {IMoveBlocksInteraction} args - The arguments for moving a block.
     * @param {IFromBlockData[]} movedBlocks - The blocks that are moved.
     * @param {number} toBlockIndex - The index of the block to which the blocks are moved.
     * @param {string} toParentId - The ID of the parent block to which the blocks are moved.
     * @param {boolean} isMovingUp - Indicates whether the blocks are moved up.
     * @returns {void}
     * @hidden
     */
    trackBlockMoveForUndoRedo(args, movedBlocks, toBlockIndex, toParentId, isMovingUp) {
        if (args.isUndoRedoAction) {
            return;
        }
        const reversedFromModels = movedBlocks.slice().reverse();
        this.pushActionIntoUndoStack({
            action: actionType.blockMoved,
            data: {
                blockIds: reversedFromModels.map((fromModel) => fromModel.blockId),
                fromIndex: reversedFromModels.map((fromModel) => fromModel.index),
                toBlockId: args.toBlockId,
                toIndex: toBlockIndex,
                fromParentId: reversedFromModels.map((fromModel) => fromModel.parent ? fromModel.parent.id : ''),
                toParentId: toParentId,
                isMovedUp: isMovingUp
            }
        });
    }
    /**
     * Triggers event notification for block transformation
     *
     * @param {HTMLElement} blockElement The block element
     * @param {BlockModel} newBlock - The new transformed block
     * @param {BlockModel} oldBlock - The old block for reference
     * @param {boolean} isUndoRedoAction - Specifies whether it is undo redo action
     * @returns {void}
     * @hidden
     */
    trackBlockTransformForUndoRedo(blockElement, newBlock, oldBlock, isUndoRedoAction) {
        if (isUndoRedoAction) {
            return;
        }
        // If in batch mode, collect the transformation instead of pushing to stack
        if (this.isBatchMode) {
            this.batchedTransforms.push({
                blockId: blockElement.id,
                oldBlockModel: oldBlock,
                newBlockModel: decoupleReference(newBlock)
            });
            return;
        }
        this.pushActionIntoUndoStack({
            action: actionType.blockTransformed,
            data: {
                blockId: blockElement.id,
                oldBlockModel: oldBlock,
                newBlockModel: decoupleReference(newBlock)
            }
        });
    }
    /**
     * Handles undo/redo recording for image insertion (placeholder → uploaded image)
     *
     * @param {string} blockId - The ID of the image block
     * @param {BlockModel} oldBlockModel - The block model before insertion (src empty)
     * @param {BlockModel} newBlockModel - The block model after insertion (src filled)
     * @returns {void}
     * @hidden
     */
    trackImageInsertionForUndoRedo(blockId, oldBlockModel, newBlockModel) {
        this.pushActionIntoUndoStack({
            action: actionType.imageInsertion,
            data: {
                blockId: blockId,
                oldBlockModel: oldBlockModel,
                newBlockModel: newBlockModel
            }
        });
    }
    /**
     * Handles undo/redo recording for clipboard paste
     *
     * @param {IAddBulkBlocksInteraction} args - The arguments of clipboard paste.
     * @returns {void}
     * @hidden
     */
    trackClipboardPasteForUndoRedo(args) {
        if (args.isUndoRedoAction) {
            return;
        }
        this.pushActionIntoUndoStack({
            action: actionType.clipboardPaste,
            oldBlockModel: args.oldBlockModel,
            data: {
                type: args.insertionType,
                blocks: decoupleReference(args.blocks.map((block) => decoupleReference(block))),
                targetBlockId: args.targetBlockId,
                isPastedAtStart: args.isPastedAtStart,
                isSelectivePaste: args.isSelectivePaste,
                cursorBlockAfterSplit: args.cursorBlockAfterSplit,
                clipboardData: {
                    blocks: args.clipboardBlocks
                }
            }
        });
    }
    /**
     * Handles indent action for undo redo
     *
     * @param {IIndentOperation} args - The arguments for indenting blocks
     * @returns {void}
     * @hidden
     */
    trackIndentActionForUndoRedo(args) {
        if (args.isUndoRedoAction) {
            return;
        }
        this.pushActionIntoUndoStack({
            action: actionType.indent,
            data: {
                blockIDs: args.blockIDs,
                shouldDecrease: args.shouldDecrease,
                isUndoRedoAction: args.isUndoRedoAction
            }
        });
    }
    /**
     * Handles Line break action for undo redo
     *
     * @param {ILineBreakOperation} args - The arguments of inserted line breaks
     * @returns {void}
     * @hidden
     */
    trackLineBreakActionForUndoRedo(args) {
        if (args.isUndoRedoAction) {
            return;
        }
        this.pushActionIntoUndoStack({
            action: actionType.lineBreakAdded,
            oldContents: args.oldContent,
            newContents: args.newContent,
            data: {
                blockId: args.blockId
            }
        });
    }
    /**
     * Handles expanded state of collapsible block for undo redo
     *
     * @param {string} blockId - The id of the block
     * @param {boolean} state - The collapsed state
     * @returns {void}
     * @hidden
     */
    trackExpandedStateForUndoRedo(blockId, state) {
        this.pushActionIntoUndoStack({
            action: actionType.isExpanded,
            data: { blockId: blockId, isExpanded: state }
        });
    }
    /**
     * Handles undo/redo recording for table block row addition
     *
     * @param {ITableRowInsertOptions} args - The arguments for adding a row.
     * @returns {void}
     * @hidden
     */
    trackTableRowInsertionForUndoRedo(args) {
        if (args.isUndoRedoAction) {
            return;
        }
        this.pushActionIntoUndoStack({
            action: actionType.tableRowInserted,
            data: { blockId: args.blockId, rowIndex: args.rowIndex, rowModel: args.rowModel }
        });
    }
    /**
     * Handles undo/redo recording for table block row deletion
     *
     * @param {ITableRowInsertOptions} args - The arguments for adding a row.
     * @returns {void}
     * @hidden
     */
    trackTableRowDeletionForUndoRedo(args) {
        if (args.isUndoRedoAction) {
            return;
        }
        this.pushActionIntoUndoStack({
            action: actionType.tableRowDeleted,
            data: { blockId: args.blockId, rowIndex: args.rowIndex, rowModel: args.rowModel }
        });
    }
    /**
     * Handles undo/redo recording for table block column addition
     *
     * @param {ITableColumnInsertOptions} args - The arguments for adding a row.
     * @returns {void}
     * @hidden
     */
    trackTableColumnInsertionForUndoRedo(args) {
        if (args.isUndoRedoAction) {
            return;
        }
        this.pushActionIntoUndoStack({
            action: actionType.tableColumnInserted,
            data: {
                blockId: args.blockId,
                colIndex: args.colIndex,
                columnModel: args.columnModel,
                columnCells: args.columnCells
            }
        });
    }
    /**
     * Handles undo/redo recording for table block row deletion
     *
     * @param {ITableColumnInsertOptions} args - The arguments for adding a row.
     * @returns {void}
     * @hidden
     */
    trackTableColumnDeletionForUndoRedo(args) {
        if (args.isUndoRedoAction) {
            return;
        }
        this.pushActionIntoUndoStack({
            action: actionType.tableColumnDeleted,
            data: {
                blockId: args.blockId,
                colIndex: args.colIndex,
                columnModel: args.columnModel,
                columnCells: args.columnCells
            }
        });
    }
    /**
     * Handles undo/redo recording for table cell clearance.
     *
     * @param {ITableCellsClearOperation} args - The arguments for table cell clearance.
     * @returns {void}
     * @hidden
     */
    trackTableCellsClearForUndoRedo(args) {
        this.pushActionIntoUndoStack({
            action: actionType.tableCellsCleared,
            data: {
                blockId: args.blockId,
                cells: args.cells
            }
        });
    }
    /**
     * Handles undo/redo recording for table cell level paste.
     *
     * @param {ITableCellsPasteOperation} args - The arguments for table cell paste.
     * @returns {void}
     * @hidden
     */
    trackTableCellsPasteForUndoRedo(args) {
        this.pushActionIntoUndoStack({
            action: actionType.tableCellsPasted,
            data: {
                blockId: args.blockId,
                cells: args.cells,
                structureDelta: args.structureDelta
            }
        });
    }
    /**
     * Handles undo/redo recording for table header input.
     *
     * @param {ITableHeaderInputOperation} args - The arguments for table header input.
     * @returns {void}
     * @hidden
     */
    trackTableHeaderInputForUndoRedo(args) {
        this.pushActionIntoUndoStack({
            action: actionType.tableHeaderInput,
            data: {
                blockId: args.blockId,
                oldColumns: args.oldColumns,
                updatedColumns: args.updatedColumns
            }
        });
    }
    /**
     * Handles undo/redo recording for table bulk row deletions
     *
     * @param {IBulkRowsDeleteOperation} args - The arguments for bulk rows deletion.
     * @returns {void}
     * @hidden
     */
    trackBulkRowDeletionForUndoRedo(args) {
        this.pushActionIntoUndoStack({
            action: actionType.tableRowsDeleted,
            data: { blockId: args.blockId, rows: args.rows }
        });
    }
    /**
     * Handles undo/redo recording for table bulk column deletions
     *
     * @param {IBulkColumnsDeleteOperation} args - The arguments for bulk column deletion.
     * @returns {void}
     * @hidden
     */
    trackBulkColumnDeletionForUndoRedo(args) {
        this.pushActionIntoUndoStack({
            action: actionType.tableColumnsDeleted,
            data: { blockId: args.blockId, cols: args.cols }
        });
    }
    /**
     * Handles undo/redo recording for table column resize operations
     *
     * @param {ITableColumnResizeOperation} data - The arguments for column resize
     * @returns {void}
     * @hidden
     */
    trackTableColumnResizeForUndoRedo(data) {
        this.pushActionIntoUndoStack({
            action: actionType.tableColumnResized,
            data
        });
    }
    /**
     * Checks whether the undo stack is empty or not.
     *
     * @returns {boolean} Returns true if the undo stack is not empty.
     * @hidden
     */
    canUndo() {
        return this.index >= 0 && this.undoRedoStack.length > 0;
    }
    /**
     * Checks whether the redo stack is empty or not.
     *
     * @returns {boolean} Returns true if the redo stack is not empty.
     * @hidden
     */
    canRedo() {
        return this.undoRedoStack.length > 0 && this.index < this.undoRedoStack.length - 1;
    }
    /**
     * Clears the undo and redo stack.
     *
     * @returns {void}
     * @hidden
     */
    clear() {
        this.undoRedoStack = [];
        this.index = -1;
    }
    destroy() {
        this.removeEventListener();
        this.clear();
        this.undoRedoManager = null;
    }
    /**
     * Adjusts undo and redo stacks to respect the new undoRedoStack limit
     *
     * @returns {void}
     * @hidden
     */
    adjustUndoRedoStacks() {
        if (this.undoRedoStack.length > this.parent.undoRedoStack) {
            // Trim from the start to keep the most recent items
            const excess = this.undoRedoStack.length - this.parent.undoRedoStack;
            this.undoRedoStack = this.undoRedoStack.slice(excess);
            this.index = Math.max(this.index - excess, -1);
        }
        // Ensure index stays within bounds
        if (this.index >= this.undoRedoStack.length) {
            this.index = this.undoRedoStack.length - 1;
        }
    }
    /**
     * Applies the next future action during redo without emitting external push/pop changes.
     * Used internally for formatting flows to replay adjacent actions.
     *
     * @returns {void}
     */
    applyNextRedoSibling() {
        // For redo, we don't need to increase the index values since those are handled automatically
        if (this.canRedo()) {
            const nextState = this.undoRedoStack[this.index];
            if (nextState) {
                this.processUndoRedoAction(nextState);
            }
        }
    }
    /**
     * Applies the previous action during undo without emitting external push/pop changes.
     * Used internally for formatting flows to replay adjacent actions.
     *
     * @returns {void}
     */
    applyNextUndoSibling() {
        if (this.canUndo()) {
            const prevState = this.undoRedoStack[this.index - 1];
            if (prevState) {
                this.processUndoRedoAction(prevState);
                this.index--;
            }
        }
    }
}
